import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ACTIONS, SCREENS } from '../../../constants/Tracking';
import { notificationTypes } from '../../../constants/notifications';
import { buildName } from '../../../utils/contacts';
import CoHostTooltip from './CoHostTooltip';
import { CountBadge, NotificationChildrenWrapper, ParticipantNotificationBadgeWrapper, UINotificationBadgeWrapper } from './Notification.style';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const matchTooltipToType = {
  [notificationTypes.coHostRequest]: CoHostTooltip
};
function Notification({
  children,
  usageTracker,
  appName,
  markNotificationAsRead,
  participantId,
  notifications,
  participants,
  myParticipantRole,
  absolute = false,
  pointer = false,
  top,
  left
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const previousTooltipState = useRef(showTooltip);
  const [activeNotification, setActiveNotification] = useState(undefined);
  const clickHandler = event => {
    event.stopPropagation();
    setShowTooltip(v => !v);
  };
  const notificationsCount = useMemo(() => {
    const notificationsIds = notifications.map(notification => notification.participantId);
    return participants.filter(participant => notificationsIds.includes(participant.participantId)).length;
  }, [participants, notifications]);
  const markAsReadHandler = useCallback(() => {
    if (activeNotification) {
      usageTracker.trackInteraction(Object.assign({
        screen: SCREENS.participants
      }, ACTIONS.participants.clickReadNotification));
      if (typeof markNotificationAsRead === 'function') {
        markNotificationAsRead(activeNotification.participantId);
      }
    }
  }, [activeNotification, usageTracker, markNotificationAsRead]);
  const closeHandler = event => {
    event.stopPropagation();
    setShowTooltip(false);
  };
  const getNotificationBadge = useCallback(({
    count,
    use
  }) => {
    if (use === 'tabs') {
      return /*#__PURE__*/_jsx(UINotificationBadgeWrapper, {
        absolute: absolute,
        top: top,
        left: left,
        pointer: pointer,
        children: /*#__PURE__*/_jsx(CountBadge, {
          "data-test-id": `${appName}-request-co-host-notification-badge`,
          children: count
        })
      });
    }
    return /*#__PURE__*/_jsx(ParticipantNotificationBadgeWrapper, {
      children: /*#__PURE__*/_jsx(CountBadge, {
        children: count
      })
    });
  }, [absolute, appName, left, pointer, top]);
  useEffect(() => {
    setActiveNotification(notifications.find(notification => notification.participantId === participantId));
  }, [notifications, participantId]);
  useEffect(() => {
    if (previousTooltipState.current && !showTooltip) {
      markAsReadHandler();
    }
    previousTooltipState.current = showTooltip;
  }, [markAsReadHandler, showTooltip]);
  if (myParticipantRole !== 'host') return /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
  if (!participantId) {
    if (!notificationsCount) {
      return /*#__PURE__*/_jsx(_Fragment, {
        children: children
      });
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [getNotificationBadge({
        count: notificationsCount,
        use: 'tabs'
      }), children]
    });
  }
  if (participantId && activeNotification && matchTooltipToType[activeNotification.type]) {
    const Wrapper = matchTooltipToType[activeNotification.type];
    const participant = participants.find(item => item.participantId === participantId);
    const participantName = participant ? buildName(participant) : 'Participant';
    return /*#__PURE__*/_jsx(Wrapper, {
      open: showTooltip,
      closeHandler: closeHandler,
      participantName: participantName,
      appName: appName,
      children: /*#__PURE__*/_jsxs(NotificationChildrenWrapper, {
        onClick: clickHandler,
        "data-test-id": `${appName}-request-co-host-notification-button`,
        children: [getNotificationBadge({
          count: 1,
          use: 'avatar'
        }), children]
      })
    });
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
}
export default Notification;